import React, { useContext, useState, useEffect } from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import { useConversations } from "./ConversationsProvider";
import { useSocket } from "./SocketProvider";
import { useBoatInfo } from "./BoatInfoProvider";
import { getDistanceFromLatLonInKm } from "../utils/utilities";

const ShipsContext = React.createContext();

export function useShips() {
  return useContext(ShipsContext);
}

export function ShipsProvider({ children }) {
  const { socket } = useSocket();
  const [socketChannels, setSocketChannels] = useState([]);
  const [ships, setShips] = useLocalStorage("ships", []);
  const [isLoading, setIsLoading] = useState(false);
  const [shipFilter, setShipFilter] = useState("");
  const [selectedShipMmsi, setSelectedShipMmsi] = useState("");
  const { conversations, addReceivedMessage, resetUnreadMessages } =
    useConversations();
  const { boatInfo } = useBoatInfo();

  useEffect(() => {
    if (socket == null || !socket.connected) return;
    const subscriptions = [];
    socketChannels.forEach((channel) => {
      console.log(`Subscribing to /positions/${channel}`);
      subscriptions.push(
        socket.subscribe(`/positions/${channel}`, (positionMesage) => {
          try {
            setIsLoading((prev) => {
              if (prev === true) return false;
              return prev;
            });
            const shipPosition = JSON.parse(positionMesage.body);
            //console.log("Received:", shipPosition);
            if (
              (shipPosition.id != null && shipPosition.id === boatInfo.id) ||
              (shipPosition.mmsi != null && shipPosition.mmsi === boatInfo.MMSI)
            ) {
              //console.log("Ignoring self position message");
            } else {
              try {
                shipPosition.distance = getDistanceFromLatLonInKm(
                  44.408036,
                  8.923026,
                  shipPosition.latitude,
                  shipPosition.longitude
                );
              } catch {}
              setShips((prev) => {
                const newShips = [...prev];
                const existingShipIndex = newShips.findIndex((ship) =>
                  shipPosition.mmsi != null
                    ? shipPosition.mmsi === ship.mmsi
                    : shipPosition.id != null
                    ? shipPosition.id === ship.id
                    : false
                );
                if (existingShipIndex >= 0) {
                  newShips[existingShipIndex] = {
                    ...newShips[existingShipIndex],
                    ...shipPosition,
                  };
                } else {
                  newShips.push({ ...shipPosition });
                }
                return newShips;
              });
            }
          } catch (err) {
            console.log("Error parsing received position message", err);
          }
        })
      );
    });

    return () => {
      console.log("Removing subscriptions...");
      subscriptions?.forEach((subscription) => subscription.unsubscribe());
    };
  }, [
    socket,
    socket?.connected,
    socketChannels,
    boatInfo,
    setShips,
    setIsLoading,
  ]);

  useEffect(() => {
    if (socket == null || !socket.connected || !boatInfo?.id) return;

    const subscription = socket.subscribe(
      `/messages/${boatInfo.id}`,
      (message) => {
        if (message != null) {
          try {
            message = JSON.parse(message.body);
            console.log(`Received message: `, message);
            // let senderMMSI =
            //   message.sender != null
            //     ? message.sender.id != null
            //       ? message.sender.id
            //       : message.sender.MMSI
            //     : null;
            let senderMMSI = message.sender;
            if (senderMMSI == null) return;
            // var found_ship = ships.find(
            //   (ship) =>
            //     (ship.mmsi != null && ship.mmsi === senderMMSI) ||
            //     (ship.id != null && ship.id === senderMMSI)
            // );
            addReceivedMessage(message, null, selectedShipMmsi);
          } catch (e) {
            console.log(`Exception while trying to read received message`, e);
          }
        }
      }
    );

    return () => subscription?.unsubscribe();
  }, [
    socket,
    socket?.connected,
    boatInfo,
    selectedShipMmsi,
    addReceivedMessage,
  ]);

  const filteredShips = ships
    .map((ship) => {
      const selected =
        (ship.mmsi != null && ship.mmsi === selectedShipMmsi) ||
        (ship.id != null && ship.id === selectedShipMmsi);
      return { ...ship, selected };
    })
    .filter((ship) => {
      if (!ship.isDummy && (ship.latitude == null || ship.longitude == null))
        return false; //Do not display ships without position
      if (shipFilter == null || shipFilter === "") return true;
      const shipName =
        ship.name != null
          ? ship.name.toLowerCase()
          : conversations[ship.mmsi != null ? ship.mmsi : ship.id] != null &&
            conversations[ship.mmsi != null ? ship.mmsi : ship.id].shipName !=
              null
          ? conversations[
              ship.mmsi != null ? ship.mmsi : ship.id
            ].shipName.toLowerCase()
          : "";
      const shipMmsi =
        ship.mmsi != null
          ? ship.mmsi.toLowerCase()
          : ship.id != null
          ? ship.id.toLowerCase()
          : "";
      return (
        shipName.includes(shipFilter.toLowerCase()) ||
        shipMmsi.includes(shipFilter.toLowerCase())
      );
    })
    .sort((s1, s2) => {
      if (
        (conversations[s1.mmsi != null ? s1.mmsi : s1.id] == null ||
          conversations[s1.mmsi != null ? s1.mmsi : s1.id]
            .lastMessageTimestamp == null) &&
        (conversations[s2.mmsi != null ? s2.mmsi : s2.id] == null ||
          conversations[s2.mmsi != null ? s2.mmsi : s2.id]
            .lastMessageTimestamp == null)
      )
        return s1.distance - s2.distance;
      if (
        conversations[s1.mmsi != null ? s1.mmsi : s1.id] == null ||
        conversations[s1.mmsi != null ? s1.mmsi : s1.id].lastMessageTimestamp ==
          null
      )
        return 1;
      if (
        conversations[s2.mmsi != null ? s2.mmsi : s2.id] == null ||
        conversations[s2.mmsi != null ? s2.mmsi : s2.id].lastMessageTimestamp ==
          null
      )
        return -1;
      return (
        conversations[s2.mmsi != null ? s2.mmsi : s2.id].lastMessageTimestamp -
        conversations[s1.mmsi != null ? s1.mmsi : s1.id].lastMessageTimestamp
      );
    });

  const shipsWithMessages = Object.keys(conversations)
    .filter(
      (key) =>
        conversations[key].messages != null &&
        conversations[key].messages.length > 0
    )
    .map((key) => {
      const selected = key === selectedShipMmsi;
      let ship = ships.find((s) =>
        s.mmsi != null ? s.mmsi === key : s.id === key
      );
      if (ship != null) {
        return {
          ...ship,
          selected,
          outOfRange: false,
        };
      } else {
        return {
          id: key,
          mmsi: key,
          name: conversations[key].shipName,
          isBaseStation: conversations[key].isBaseStation,
          selected,
          outOfRange: true,
        };
      }
    })
    .filter((ship) => {
      if (shipFilter == null || shipFilter === "") return true;
      const shipName =
        ship.name != null
          ? ship.name.toLowerCase()
          : conversations[ship.mmsi != null ? ship.mmsi : ship.id] != null &&
            conversations[ship.mmsi != null ? ship.mmsi : ship.id].shipName !=
              null
          ? conversations[
              ship.mmsi != null ? ship.mmsi : ship.id
            ].shipName.toLowerCase()
          : "";
      const shipMmsi =
        ship.mmsi != null
          ? ship.mmsi.toLowerCase()
          : ship.id != null
          ? ship.id.toLowerCase()
          : "";
      return (
        shipName.includes(shipFilter.toLowerCase()) ||
        shipMmsi.includes(shipFilter.toLowerCase())
      );
    })
    .sort((s1, s2) => {
      if (
        conversations[s1.mmsi != null ? s1.mmsi : s1.id].lastMessageTimestamp ==
          null &&
        conversations[s2.mmsi != null ? s2.mmsi : s2.id].lastMessageTimestamp ==
          null
      )
        return 0;
      if (
        conversations[s1.mmsi != null ? s1.mmsi : s1.id].lastMessageTimestamp ==
        null
      )
        return 1;
      if (
        conversations[s2.mmsi != null ? s2.mmsi : s2.id].lastMessageTimestamp ==
        null
      )
        return -1;
      return (
        conversations[s2.mmsi != null ? s2.mmsi : s2.id].lastMessageTimestamp -
        conversations[s1.mmsi != null ? s1.mmsi : s1.id].lastMessageTimestamp
      );
    });

  function selectShipMmsi(mmsi) {
    setSelectedShipMmsi(mmsi);
    resetUnreadMessages(mmsi);
  }

  function resetShipsList() {
    setShips([]);
  }

  const value = {
    ships: filteredShips,
    unfilteredShips: ships,
    isLoading,
    shipFilter,
    filterShips: setShipFilter,
    selectShipMmsi,
    selectedShip:
      filteredShips.find((ship) =>
        ship.mmsi != null
          ? ship.mmsi === selectedShipMmsi
          : ship.id === selectedShipMmsi
      ) ||
      shipsWithMessages.find((ship) =>
        ship.mmsi != null
          ? ship.mmsi === selectedShipMmsi
          : ship.id === selectedShipMmsi
      ),
    shipsWithMessages,
    socketChannels,
    setSocketChannels,
    resetShipsList,
  };

  return (
    <ShipsContext.Provider value={value}>{children}</ShipsContext.Provider>
  );
}
